import axios from 'axios';
import store from 'src/store';
import { logout, userAuthRequestFailed } from 'src/store/auth';
import { showToastAction } from 'src/store/ui';

let responseInterceptorId;

export const configureNetworkSettingsForAuthenticatedUser = () => {
    setHeader();
    setResponseInterceptor();
};

// AUTH HEADER WITH EVERY REQUEST
const setHeader = () => {
    const authState = store.getState().auth;
    axios.defaults.headers.common[
        'Authorization'
    ] = `Bearer ${authState.token}`;
};

// response interceptor
const setResponseInterceptor = () => {
    // we have to use type of because the id can be 0
    if (typeof responseInterceptorId != 'undefined') {
        // interceptor already created, this method can be called twice
        // incase there is any error in DOM
        // probably not gonna happen in prod
        return;
    }

    responseInterceptorId = axios.interceptors.response.use(
        res => res,
        err => {
            logNetworkError(err);

            if (err.response && err.response.status === 401) {
                store.dispatch(
                    userAuthRequestFailed(
                        'You are logged out because your session has expired. Kindly log in again',
                    ),
                );
                store.dispatch(logout());
            } else {
                store.dispatch(
                    showToastAction({
                        type: 'danger',
                        title: 'Error',
                        text: generateErrorText(err),
                    }),
                );
            }

            return Promise.reject(err);
        },
    );
};

// this will be called from logout side effect
export const cancelAxiosResponseInterceptor = () => {
    axios.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = null;
};

const logNetworkError = ({ response, config, message }) => {
    // A network/CORS failure has no response, so fall back to the request config.
    const requestConfig = response ? response.config : config;
    const target = `${requestConfig?.baseURL || ''}${requestConfig?.url || ''}`;
    console.log(
        response
            ? `Call to ${target} failed with status code ${response.status}`
            : `Call to ${target} failed without a response: ${message}`,
    );
};

// Prettify a Stripe decline code like "insufficient_funds" -> "Insufficient funds".
const humanizeCode = code =>
    typeof code === 'string' && code.length
        ? code.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())
        : '';

// Detect and format a Stripe error object forwarded through the API.
// Returns a user-facing string, or null if this doesn't look like one.
const extractStripeErrorText = data => {
    if (!data || typeof data !== 'object') return null;
    const isStripe =
        (typeof data.type === 'string' && data.type.startsWith('Stripe')) ||
        data.rawType === 'card_error' ||
        data.raw?.type === 'card_error';
    if (!isStripe) return null;

    const stripeMsg = data.raw?.message || data.message || 'Card was declined.';
    const declineCode = data.decline_code || data.raw?.decline_code;
    const code = data.code || data.raw?.code;
    const codeLabel = humanizeCode(declineCode || code);
    return codeLabel
        ? `Payment declined · ${codeLabel} — ${stripeMsg}`
        : `Payment declined · ${stripeMsg}`;
};

const generateErrorText = ({ response, message }) => {
    if (!response) {
        return message
            ? `Could not reach the server (${message})`
            : 'something went wrong';
    }

    if (!response.data) {
        return response.statusText;
    }

    if (typeof response.data === 'string') {
        return response.data;
    }

    if (typeof response.data === 'object') {
        const stripeText = extractStripeErrorText(response.data);
        if (stripeText) return stripeText;
        if (typeof response.data.message === 'string') {
            return response.data.message;
        }
        if (typeof response.data.error === 'string') {
            return response.data.error;
        }
    }

    return 'something went wrong';
};
